import {createSlice, createAsyncThunk, current} from "@reduxjs/toolkit"
import moment from "moment"

import {post, get} from "helpers/api"

export const initialState = {
	users: {
		loading: true,
		total: 0,
		data: []
	},
	bookings: [],
	notes: [],
	migrationCodes: [],
	userDetails: {},
	allUsersFiltered: {
		loading: true,
		total: 0,
		data: []
	},
	toggleDashboardApiState: {
		isFetching: true,
		isSuccess: false,
		isError: false,
		message: ""
	},
	intentNotificationHistory: {
		loading: true,
		total: 0,
		data: []
	},
	userDisableUserApiState: {
		isFetching: true,
		isSuccess: false,
		isError: false,
		message: ""
	},
	usersApiState: {
		isFetching: true,
		isSuccess: false,
		isError: false,
		message: ""
	},
	allUsersFilteredApiState: {
		isFetching: true,
		isSuccess: false,
		isError: false,
		message: ""
	},
	userDetailsState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	userNotificationState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	userBookingsApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	createNewUserApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	updateUserApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	updatedSessionApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	requestSessionsApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	fetchSessionDetailsApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	userNotesApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	userMigrationApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	createUserMigrationApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		message: ""
	},
	fetchCodeCapsApiState: {
		isFetching: false,
		isSuccess: false,
		isError: false,
		coachingCallCap: 0,
		therapyCallCap: 0,
		startingDate: moment().toISOString(),
		combined: true,
		message: ""
	},
	profile: {}
}

export const fetchUsersData = createAsyncThunk(
	"users/fetchUsersData",
	async ({coachId, limit, skip, search, user, inActive, token}, thunkAPI) => {
		const encodeSearch = encodeURIComponent(search)
		let response = await get(
			`v2/users/${coachId}?limit=${limit}&inActive=${inActive}&skip=${skip}&search=${encodeSearch}&isAdmin=${user["https://app.uprise.co/app_metadata"].isAdmin}`,
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const fetchUserIntentHistory = createAsyncThunk(
	"users/fetchUserIntentHistory",
	async ({email, limit, skip, token}, thunkAPI) => {
		let response = await get(
			`v2/user/workable/intent-history/${email}?limit=${limit}&skip=${skip}`,
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const fetchUsersByEmail = createAsyncThunk("users/fetchUsersByEmail", async ({email, token}, thunkAPI) => {
	const response = await get(`v2/user/${email}`, {}, {Authorization: `Bearer ${token}`})

	if (response.error) {
		return thunkAPI.rejectWithValue(response)
	} else {
		return response
	}
})

export const fetchBookingsByUserEmail = createAsyncThunk(
	"users/fetchBookingsByUserEmail",
	async ({userEmail, token}, thunkAPI) => {
		try {
			const response = await get(`v2/bookings/user/${userEmail}`, {}, {Authorization: `Bearer ${token}`})

			if (response.error) {
				return thunkAPI.rejectWithValue(response)
			} else {
				return response
			}
		} catch (error) {
			return thunkAPI.rejectWithValue(error)
		}
	}
)

export const fetchBookingsByUserId = createAsyncThunk(
	"users/fetchBookingsByUserEmail",
	async ({userId, token}, thunkAPI) => {
		try {
			const response = await get(`v2/bookings/userId/${userId}`, {}, {Authorization: `Bearer ${token}`})

			if (response.error) {
				return thunkAPI.rejectWithValue(response)
			} else {
				return response
			}
		} catch (error) {
			return thunkAPI.rejectWithValue(error)
		}
	}
)

export const fetchUsersDataFiltered = createAsyncThunk(
	"users/fetchUsersDataFiltered",
	async ({coachId, employerCode, limit, skip, user, search, inActive, token}, thunkAPI) => {
		let response = await get(
			`v2/users/${coachId}/${employerCode}?limit=${limit}&inActive=${inActive}&skip=${skip}&isAdmin=${user["https://app.uprise.co/app_metadata"].isAdmin}&search=${search}`,
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const disableUser = createAsyncThunk("users/disableUser", async ({email, disabled, token}, thunkAPI) => {
	const response = await post(`v2/user/status`, {email, disabled}, {}, {Authorization: `Bearer ${token}`})

	if (response.error) {
		return thunkAPI.rejectWithValue(response)
	} else {
		return response
	}
})

export const toggleDashboardViewForUser = createAsyncThunk(
	"users/toggleDashboardViewForUser",
	async ({email, isDashboardViewEnabled, token}, thunkAPI) => {
		const response = await post(
			`v2/user/hr/status`,
			{email, isDashboardViewEnabled},
			{},
			{Authorization: `Bearer ${token}`}
		)

		return response
	}
)

export const fetchUsersByCourse = createAsyncThunk(
	"users/fetchUsersByCourse",
	async ({coachId, course, limit, skip, user, token}, thunkAPI) => {
		let response = await get(
			`v2/users/${coachId}/course/${course}?limit=${limit}&skip=${skip}&isAdmin=${user["https://app.uprise.co/app_metadata"].isAdmin}`,
			{},
			{Authorization: `Bearer ${token}`}
		)

		return response
	}
)
export const fetchUserNotes = createAsyncThunk(
	"users/fetchUserNotes",
	async ({userEmail, coachId, token}, thunkAPI) => {
		let response = await get(`v2/notes/${userEmail}/${coachId}`, {}, {Authorization: `Bearer ${token}`})
		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const createUserNotes = createAsyncThunk(
	"users/createUserNotes",
	async ({userEmail, coachId, title, content, token}, thunkAPI) => {
		let response = await post(
			"v2/notes/create",
			{
				client: userEmail,
				coachId: coachId,
				title: title,
				content: content
			},
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error)
		} else {
			return response
		}
	}
)

export const deleteUserNote = createAsyncThunk("users/deleteUserNotes", async ({id, token}, thunkAPI) => {
	let response = await post("v2/notes/delete", {id}, {}, {Authorization: `Bearer ${token}`})

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error)
	} else {
		return response
	}
})

export const updateUserNote = createAsyncThunk(
	"users/updateUserNotes",
	async ({id, userEmail, coachId, title, content, token, autosave}, thunkAPI) => {
		let response = await post(
			"v2/notes/update",
			{
				id,
				client: userEmail,
				coachId: coachId,
				title: title,
				content: content
			},
			{},
			{Authorization: `Bearer ${token}`}
		)
		if (response.error) {
			return thunkAPI.rejectWithValue(response.error)
		} else if (!response.error && autosave) {
			return {...response, message: ""}
		} else {
			return {...response, message: "Notes Updated successfully"}
		}
	}
)

export const createNewUser = createAsyncThunk(
	"user/new",
	async ({email, name, phone, countryCode, countryName, employerCode, timezone, password, token}, thunkAPI) => {
		const response = await post(
			`v2/user/create`,
			{email, name, phone, countryCode, countryName, employerCode, timezone, password},
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const updateUserInfo = createAsyncThunk(
	"user/update",
	async ({currentEmail, email, name, phone, countryCode, token}, thunkAPI) => {
		const response = await post(
			`v2/user/update`,
			{currentEmail, email, name, phone, countryCode},
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const updateSessionDetails = createAsyncThunk(
	"bookings/sessionDeatails/update",
	async ({data, token}, thunkAPI) => {
		const response = await post(`v2/bookings/completeSessions/create`, data, {}, {Authorization: `Bearer ${token}`})

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const updateSessionNotes = createAsyncThunk(
	"bookings/updateSessionNotes/update",
	async ({data, token}, thunkAPI) => {
		const response = await post(`v2/bookings/sessionNotes/update`, data, {}, {Authorization: `Bearer ${token}`})

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const fetchSessionDetailsByUser = createAsyncThunk(
	"bookings/sessionNotes/:email",
	async ({email, token}, thunkAPI) => {
		let response = await get(`v2/bookings/sessionNotes/${email}`, {}, {Authorization: `Bearer ${token}`})
		if (response.status === 200) {
			return response
		} else {
			return thunkAPI.rejectWithValue("Error while fetching Session Notes")
		}
	}
)

export const requestAdditionalSessions = createAsyncThunk(
	"bookings/additionalSessions/create",
	async ({data, token}, thunkAPI) => {
		const response = await post(
			`v2/bookings/additionalSessions/create`,
			data,
			{},
			{Authorization: `Bearer ${token}`}
		)

		if (response.error) {
			return thunkAPI.rejectWithValue(response)
		} else {
			return response
		}
	}
)

export const fetchAvailableMigrationCodes = createAsyncThunk(
	"users/fetchAvailableMigrationCodes",
	async ({limit, skip, search, token}, thunkAPI) => {
		let response = await get(
			`v2/codes?limit=${limit}&skip=${skip}&search=${search}`,
			{},
			{Authorization: `Bearer ${token}`}
		)
		if (response.error) {
			return thunkAPI.rejectWithValue("Error while fetching Migration Codes")
		} else {
			return response
		}
	}
)

export const fetchCodeCaps = createAsyncThunk("users/fetchCodeCaps", async ({code, token}, thunkAPI) => {
	let response = await get(`v2/codes/caps/${code}`, {}, {Authorization: `Bearer ${token}`})
	if (response.error) {
		return thunkAPI.rejectWithValue("Error while fetching Code Caps")
	} else {
		return response
	}
})

export const createUserMigration = createAsyncThunk("users/createUserMigration", async ({data, token}, thunkAPI) => {
	let response = await post("v2/user/migrate", data, {}, {Authorization: `Bearer ${token}`})

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error)
	} else {
		return response
	}
})

export const userSlice = createSlice({
	name: "user",
	initialState: initialState,
	reducers: {
		clearToggleDashUserState: state => {
			state.toggleDashboardApiState.isSuccess = false
			state.toggleDashboardApiState.isError = false
			state.toggleDashboardApiState.isFetching = false
			state.toggleDashboardApiState.message = ""
			return state
		},
		clearDisableUserState: state => {
			state.userDisableUserApiState.isSuccess = false
			state.userDisableUserApiState.isError = false
			state.userDisableUserApiState.isFetching = false
			state.userDisableUserApiState.message = ""
			return state
		},
		clearUserState: state => {
			state.profile = null
			return state
		},
		clearUserDetailsState: state => {
			state.userDetailsState.isSuccess = false
			state.userDetailsState.isError = false
			state.userDetailsState.isFetching = false
			state.userDetailsState.message = ""
			return state
		},
		clearUserNotificationState: state => {
			state.userNotificationState.isSuccess = false
			state.userNotificationState.isError = false
			state.userNotificationState.isFetching = false
			state.userNotificationState.message = ""
			return state
		},
		clearUsersApiState: state => {
			state.usersApiState.isSuccess = false
			state.usersApiState.isError = false
			state.usersApiState.isFetching = true
			state.usersApiState.message = ""

			return state
		},
		clearNewUserApiState: state => {
			state.createNewUserApiState.isSuccess = false
			state.createNewUserApiState.isError = false
			state.createNewUserApiState.isFetching = false
			state.createNewUserApiState.message = ""
			return state
		},
		clearupdateUserApiState: state => {
			state.updateUserApiState.isSuccess = false
			state.updateUserApiState.isError = false
			state.updateUserApiState.isFetching = false
			state.updateUserApiState.message = ""
			return state
		},
		clearUserNotesApiState: state => {
			state.userNotesApiState.isSuccess = false
			state.userNotesApiState.isError = false
			state.userNotesApiState.isFetching = false
			state.userNotesApiState.message = ""
			return state
		},
		clearAllUsersFilteredApiState: state => {
			state.allUsersFilteredApiState.isSuccess = false
			state.allUsersFilteredApiState.isError = false
			state.allUsersFilteredApiState.isFetching = false
			state.allUsersFilteredApiState.data = []
			state.allUsersFilteredApiState.message = ""
			return state
		},
		clearUpdateSessionApiState: state => {
			state.updatedSessionApiState.isSuccess = false
			state.updatedSessionApiState.isError = false
			state.updatedSessionApiState.isFetching = false
			state.updatedSessionApiState.message = ""
			return state
		},
		clearRequestAdditionalSessions: state => {
			state.requestSessionsApiState.isSuccess = false
			state.requestSessionsApiState.isError = false
			state.requestSessionsApiState.isFetching = false
			state.requestSessionsApiState.message = ""
			return state
		}
	},
	extraReducers: {
		[disableUser.pending]: state => {
			state.userDisableUserApiState.isFetching = true
		},
		[disableUser.rejected]: (state, {payload}) => {
			state.userDisableUserApiState.isFetching = false
			state.userDisableUserApiState.isError = true

			if (payload?.error) {
				state.userDisableUserApiState.message = payload?.message
			} else {
				state.userDisableUserApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[disableUser.fulfilled]: (state, {payload}) => {
			state.userDisableUserApiState.isFetching = false
			state.userDisableUserApiState.isSuccess = true
			state.userDisableUserApiState.message = "User status updated"

			const updateToggle = data => {
				let id
				const item = data.find((el, idx) => {
					if (el?.email === payload.email) {
						id = idx
						return true
					}
				})
				if (item) {
					data[id].isAccountDisabled = payload.disabled
					data[id].status = payload.disabled ? "Inactive" : "Active"
				}
			}
			if (state.allUsersFilteredApiState.data) {
				updateToggle(state.allUsersFilteredApiState.data)
			}
			if (state.users.data) updateToggle(state.users.data)
			if (state.profile)
				// user profile
				state.profile.disabled = payload.disabled
		},
		[toggleDashboardViewForUser.pending]: state => {
			state.toggleDashboardApiState.isFetching = true
			state.toggleDashboardApiState.isError = false
			state.toggleDashboardApiState.isSuccess = false
		},
		[toggleDashboardViewForUser.rejected]: (state, {payload}) => {
			state.toggleDashboardApiState.isFetching = false
			state.toggleDashboardApiState.isError = true
			state.toggleDashboardApiState.isSuccess = false

			if (payload?.error) {
				state.toggleDashboardApiState.message = payload?.message
			} else {
				state.toggleDashboardApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[toggleDashboardViewForUser.fulfilled]: (state, {payload}) => {
			state.toggleDashboardApiState.isFetching = false
			state.toggleDashboardApiState.isSuccess = true
			state.toggleDashboardApiState.isError = false
			state.toggleDashboardApiState.message = "Successfully updated dashboard status"

			const updateToggle = data => {
				let id
				const item = data.find((el, idx) => {
					if (el?.email === payload.email) {
						id = idx
						return true
					}
				})
				if (item) data[id].isDashboardViewEnabled = payload.isDashboardViewEnabled
			}
			if (state.allUsersFiltered.data) {
				updateToggle(state.allUsersFiltered.data)
			}
			if (state.users.data) updateToggle(state.users.data)
			if (state.profile) state.profile.isDashboardViewEnabled = payload.isDashboardViewEnabled
		},
		[fetchUserNotes.pending]: state => {
			state.userNotesApiState.isFetching = true
		},
		[fetchUserNotes.fulfilled]: (state, {payload}) => {
			const {data} = payload
			state.userNotesApiState.isFetching = false

			state.notes = data || []
		},
		[fetchUserNotes.rejected]: (state, {payload}) => {
			state.userNotesApiState.isFetching = false
			state.userNotesApiState.isError = true

			if (payload?.error) {
				state.userNotesApiState.message = payload?.message
			} else {
				state.userNotesApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[createUserNotes.fulfilled]: (state, {payload}) => {
			const {data} = payload

			state.userNotesApiState.isFetching = false
			state.userNotesApiState.isSuccess = true
			state.userNotesApiState.message = "User Notes created successfully"

			state.notes = [...state.notes, data]
		},
		[createUserNotes.rejected]: state => {
			state.userNotesApiState.isFetching = false
			state.userNotesApiState.isError = true
			state.userNotesApiState.isSuccess = false
		},
		[createUserNotes.pending]: state => {
			state.userNotesApiState.isFetching = true
		},
		[updateUserNote.fulfilled]: (state, {payload}) => {
			state.userNotesApiState.isFetching = false
			state.userNotesApiState.isSuccess = true
			state.userNotesApiState.message = payload?.message
		},
		[updateUserNote.rejected]: state => {
			state.userNotesApiState.isError = true
			state.userNotesApiState.isFetching = false
		},
		[updateUserNote.pending]: state => {
			state.userNotesApiState.isFetching = true
		},
		[deleteUserNote.fulfilled]: (state, {payload}) => {
			const {id} = payload
			state.userNotesApiState.isFetching = false
			state.userNotesApiState.isSuccess = true
			state.notes = state.notes.filter(note => note.id !== id)
			state.userNotesApiState.message = "Note Deleted Successfully"
			return state
		},
		[deleteUserNote.rejected]: state => {
			state.userNotesApiState.isError = true
			state.userNotesApiState.message = "Something went wrong on deleting notes"
		},
		[deleteUserNote.pending]: state => {
			state.userNotesApiState.isFetching = true
		},
		[fetchUsersData.pending]: state => {
			state.usersApiState.isError = false
			state.usersApiState.isFetching = true
			state.usersApiState.isSuccess = false
		},
		[fetchUsersData.rejected]: state => {
			state.usersApiState.isFetching = false
			state.usersApiState.isError = true
			state.usersApiState.isSuccess = false
		},
		[fetchUsersData.fulfilled]: (state, {payload}) => {
			state.usersApiState.isFetching = false
			state.usersApiState.isSuccess = true
			state.users.data = payload.data.map(user => {
				let status = "Inactive"
				if (user.isAccountDisabled) status = "Inactive"
				else status = "Active"
				return {...user, status}
			})
			state.users.total = payload.totalCount
		},
		[fetchBookingsByUserEmail.pending]: state => {
			state.userBookingsApiState.loading = true
		},
		[fetchBookingsByUserEmail.rejected]: state => {
			state.userBookingsApiState.isError = true
			state.userBookingsApiState.message = "Error retrieving bookings"
			state.userBookingsApiState.isSuccess = false
			state.userBookingsApiState.isFetching = false
		},
		[fetchBookingsByUserEmail.fulfilled]: (state, {payload}) => {
			state.userBookingsApiState.isFetching = false
			state.userBookingsApiState.isSuccess = true
			state.bookings = payload?.data
		},
		[fetchBookingsByUserId.pending]: state => {
			state.userBookingsApiState.loading = true
		},
		[fetchBookingsByUserId.rejected]: state => {
			state.userBookingsApiState.isError = true
			state.userBookingsApiState.message = "Error retrieving bookings"
			state.userBookingsApiState.isSuccess = false
			state.userBookingsApiState.isFetching = false
		},
		[fetchBookingsByUserId.fulfilled]: (state, {payload}) => {
			state.userBookingsApiState.isFetching = false
			state.userBookingsApiState.isSuccess = true
			state.bookings = payload?.data
		},
		[fetchUsersByEmail.pending]: state => {
			state.userDetailsState.isFetching = true
		},
		[fetchUsersByEmail.rejected]: (state, {payload}) => {
			state.userDetailsState.isError = true
			state.userDetailsState.isFetching = false

			if (payload?.error) {
				state.userDetailsState.message = payload?.message
			} else {
				state.userDetailsState.message = "Something went wrong. Please try after sometime"
			}
		},
		[fetchUsersByEmail.fulfilled]: (state, {payload}) => {
			const {
				userId,
				name,
				email,
				countryCode,
				countryName,
				phone,
				companyName,
				employerCode,
				disableCallCap,
				version,
				disable,
				surveyResults,
				tasks,
				companyCallCap,
				timezone,
				isDashboardViewEnabled,
				coach,
				totalExtraSessions,
				extraSessions,
				sessionsLeft,
				isEmployerCodeExpired
			} = payload.data

			state.results = surveyResults
			state.tasks = tasks

			state.profile = {
				userId,
				name,
				email,
				countryCode,
				countryName,
				phone,
				companyName,
				employerCode,
				disabled: disable,
				version: version ? version : "AU",
				disableCallCap,
				companyCallCap,
				timezone: timezone || "Australia/Sydney",
				isDashboardViewEnabled: isDashboardViewEnabled,
				coach,
				totalExtraSessions,
				extraSessions,
				sessionsLeft,
				isEmployerCodeExpired
			}

			state.userDetailsState.isFetching = false
			state.userDetailsState.isSuccess = true
			state.userDetailsState.isError = false
		},

		[fetchUsersDataFiltered.pending]: state => {
			state.allUsersFilteredApiState.isFetching = true
			state.userDetailsState.isSuccess = false
			state.userDetailsState.isError = false
		},
		[fetchUsersDataFiltered.rejected]: (state, {payload}) => {
			state.allUsersFilteredApiState.isFetching = false
			state.allUsersFilteredApiState.isError = true
			state.allUsersFilteredApiState.message = payload?.message
		},
		[fetchUsersDataFiltered.fulfilled]: (state, {payload}) => {
			state.allUsersFilteredApiState.isFetching = false
			state.allUsersFilteredApiState.isSuccess = true
			state.allUsersFilteredApiState.data = payload.data.map(user => {
				let status = "Active"
				if (user.isAccountDisabled) status = "Inactive"
				else status = "Active"
				return {...user, status}
			})
			state.allUsersFilteredApiState.total = payload.totalCount
		},
		[fetchUsersByCourse.fulfilled]: (state, {payload}) => {
			state.apiState.isFetching = false
			state.apiState.isSuccess = true
			state.users.data = payload.data
			state.users.total = payload.totalCount
		},
		[fetchUsersByCourse.rejected]: state => {},
		[fetchUsersByCourse.pending]: state => {
			state.apiState.isFetching = false
			state.apiState.isError = true
		},
		[createNewUser.pending]: state => {
			state.createNewUserApiState.isFetching = true
		},
		[createNewUser.rejected]: (state, {payload}) => {
			state.createNewUserApiState.isFetching = false
			state.createNewUserApiState.isError = true

			if (payload?.error) {
				if (payload?.errors?.length > 0) {
					state.createNewUserApiState.message = payload?.errors[0]?.stack
				} else {
					state.createNewUserApiState.message = payload?.message
				}
			} else {
				state.createNewUserApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[createNewUser.fulfilled]: state => {
			state.createNewUserApiState.isFetching = false
			state.createNewUserApiState.isSuccess = true
			state.createNewUserApiState.message = "User created successfully"
		},
		[updateUserInfo.pending]: state => {
			state.updateUserApiState.isFetching = true
		},
		[updateUserInfo.rejected]: (state, {payload}) => {
			state.updateUserApiState.isFetching = false
			state.updateUserApiState.isError = true

			if (payload?.error) {
				if (payload?.errors?.length > 0) {
					state.updateUserApiState.message = payload?.errors[0]?.stack
				} else {
					state.updateUserApiState.message = payload?.message
				}
			} else {
				state.updateUserApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[updateUserInfo.fulfilled]: (state, {payload}) => {
			state.updateUserApiState.isFetching = false
			state.updateUserApiState.isSuccess = true
			state.profile.name = payload.data.data.name
			state.profile.phone = payload.data.data.phone
			state.profile.countryCode = payload.data.data.countryCode
			state.updateUserApiState.message = "User updated successfully"
		},
		[updateSessionDetails.pending]: state => {
			state.updatedSessionApiState.isFetching = true
			state.updatedSessionApiState.isSuccess = false
		},
		[updateSessionDetails.rejected]: (state, {payload}) => {
			state.updatedSessionApiState.isFetching = false
			state.updatedSessionApiState.isError = true
			state.updatedSessionApiState.isSuccess = false

			if (payload?.error) {
				state.updatedSessionApiState.message = payload?.message
			} else {
				state.updatedSessionApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[updateSessionDetails.fulfilled]: (state, {payload}) => {
			state.updatedSessionApiState.isFetching = false
			state.updatedSessionApiState.isSuccess = true
			state.updatedSessionApiState.message = "Session Details saved successfully"
		},
		[updateSessionNotes.pending]: state => {
			state.updatedSessionApiState.isFetching = true
			state.updatedSessionApiState.isSuccess = false
		},
		[updateSessionNotes.rejected]: (state, {payload}) => {
			state.updatedSessionApiState.isFetching = false
			state.updatedSessionApiState.isError = true
			state.updatedSessionApiState.isSuccess = false

			if (payload?.error) {
				state.updatedSessionApiState.message = payload?.message
			} else {
				state.updatedSessionApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[updateSessionNotes.fulfilled]: (state, {payload}) => {
			state.updatedSessionApiState.isFetching = false
			state.updatedSessionApiState.isSuccess = true
			state.updatedSessionApiState.message = "Session Notes saved successfully"
		},
		[requestAdditionalSessions.pending]: state => {
			state.requestSessionsApiState.isFetching = true
			state.requestSessionsApiState.isSuccess = false
		},
		[requestAdditionalSessions.rejected]: (state, {payload}) => {
			state.requestSessionsApiState.isFetching = false
			state.requestSessionsApiState.isError = true
			state.requestSessionsApiState.isSuccess = false

			if (payload?.error) {
				state.requestSessionsApiState.message = payload?.message
			} else {
				state.requestSessionsApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[requestAdditionalSessions.fulfilled]: (state, {payload}) => {
			state.requestSessionsApiState.isFetching = false
			state.requestSessionsApiState.isSuccess = true
			state.requestSessionsApiState.message = "Additional Session Details saved successfully"
		},
		[fetchCodeCaps.pending]: state => {
			state.fetchCodeCapsApiState.isFetching = true
			state.fetchCodeCapsApiState.isSuccess = false
		},
		[fetchCodeCaps.rejected]: (state, {payload}) => {
			state.fetchCodeCapsApiState.isFetching = false
			state.fetchCodeCapsApiState.isError = true
			state.fetchCodeCapsApiState.isSuccess = false
			state.fetchCodeCapsApiState.coachingCallCap = 0
			state.fetchCodeCapsApiState.therapyCallCap = 0
			state.fetchCodeCapsApiState.startingDate = moment().toISOString()
			state.fetchCodeCapsApiState.combined = true

			if (payload?.error) {
				state.fetchCodeCapsApiState.message = payload?.message
			} else {
				state.fetchCodeCapsApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[fetchCodeCaps.fulfilled]: (state, {payload}) => {
			state.fetchCodeCapsApiState.isFetching = false
			state.fetchCodeCapsApiState.isSuccess = true
			state.fetchCodeCapsApiState.message = "Code Caps fetched successfully"
			const caps = payload.data
			state.fetchCodeCapsApiState.coachingCallCap = caps.coachingCallCap
			state.fetchCodeCapsApiState.therapyCallCap = caps.therapyCallCap
			state.fetchCodeCapsApiState.startingDate = caps.startingDate
			state.fetchCodeCapsApiState.combined = caps.combined
		},
		[fetchSessionDetailsByUser.pending]: state => {
			state.fetchSessionDetailsApiState.isFetching = true
			state.fetchSessionDetailsApiState.success = false
		},
		[fetchSessionDetailsByUser.rejected]: (state, {payload}) => {
			state.fetchSessionDetailsApiState.isFetching = false
			state.fetchSessionDetailsApiState.isError = true

			if (payload?.error) {
				state.fetchSessionDetailsApiState.message = payload?.message
			} else {
				state.fetchSessionDetailsApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[fetchSessionDetailsByUser.fulfilled]: (state, {payload}) => {
			state.fetchSessionDetailsApiState.isFetching = false
			state.fetchSessionDetailsApiState.isSuccess = true
			state.fetchSessionDetailsApiState.message = "Session Details saved successfully"
			state.fetchSessionDetailsApiState.data = payload.data
		},
		[fetchAvailableMigrationCodes.pending]: state => {
			state.userMigrationApiState.isFetching = true
		},
		[fetchAvailableMigrationCodes.fulfilled]: (state, {payload}) => {
			const {data} = payload
			state.userMigrationApiState.isFetching = false
			state.migrationCodes = data || []
		},
		[fetchAvailableMigrationCodes.rejected]: (state, {payload}) => {
			state.userMigrationApiState.isFetching = false
			state.userMigrationApiState.isError = true
			if (payload?.error) {
				state.userMigrationApiState.message = payload?.message
			} else {
				state.userMigrationApiState.message = "Something went wrong. Please try after sometime"
			}
		},
		[createUserMigration.pending]: state => {
			state.createUserMigrationApiState.isFetching = true
		},
		[createUserMigration.fulfilled]: (state, {payload}) => {
			const {data} = payload
			state.createUserMigrationApiState.isFetching = false
		},
		[createUserMigration.rejected]: (state, {payload}) => {
			state.createUserMigrationApiState.isFetching = false
			state.createUserMigrationApiState.isError = true
			if (payload?.error) {
				state.createUserMigrationApiState.message = payload?.message
			} else {
				state.createUserMigrationApiState.message = "Something went wrong. Please try after sometime"
			}
		}
	}
})

export const {
	clearUserState,
	clearUsersApiState,
	clearUserDetailsState,
	clearToggleDashUserState,
	clearUserNotificationState,
	clearNewUserApiState,
	clearUserNotesApiState,
	clearDisableUserState,
	clearupdateUserApiState,
	clearAllUsersFilteredApiState,
	clearUpdateSessionApiState,
	clearRequestAdditionalSessions
} = userSlice.actions

export const userDataSelector = state => state.user

export default userSlice
